import React, { createContext, useState, useEffect, useContext, useCallback  } from 'react';
import axios from 'axios';

const RechargeOptionsContext = createContext();

export const useRechargeOptions = () => {
    return useContext(RechargeOptionsContext);
};

export const RechargeOptionsProvider = ({ children }) => {
    const [rechargeOptions, setRechargeOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('userId');

            try {
                const rechargeResponse = await axios.get(`${process.env.REACT_APP_API_URL}/recharge-options`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'User-ID': userId,
                    },
                });
                setRechargeOptions(rechargeResponse.data);

                const walletResponse = await axios.get(`${process.env.REACT_APP_API_URL}/wallet-balance`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'User-ID': userId,
                    },
                });
                setWalletBalance(walletResponse.data.balance);

                setLoading(false);
            } catch (error) {
                setError('Error fetching data');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Function to fetch wallet balance once (reusable)
    const fetchWalletBalance = useCallback(async () => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        try {
            const walletResponse = await axios.get(`${process.env.REACT_APP_API_URL}/wallet-balance`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'User-ID': userId,
                },
            });
            setWalletBalance(walletResponse.data.balance);
        } catch (error) {
            console.error('Error fetching wallet balance:', error);
        }
    }, []);

    // Function to start periodic balance check every 15 seconds (for 5 minutes)
    const startBalanceCheck = useCallback((originalBalance) => {
        let intervalId;
        let timeElapsed = 0;

        // Check wallet balance every 15 seconds, stop after 5 minutes (300,000 ms)
        intervalId = setInterval(async () => {
            timeElapsed += 15000; // Increment time elapsed by 15 seconds (15000 ms)

            try {
                await fetchWalletBalance(); // Fetch wallet balance

                // If the balance is updated or time exceeds 5 minutes, stop the interval
                if (walletBalance !== originalBalance || timeElapsed >= 300000) {
                    clearInterval(intervalId);
                }
            } catch (error) {
                console.error('Error fetching wallet balance during periodic check:', error);
            }
        }, 15000); // Fetch every 15 seconds

        // Automatically stop checking after 5 minutes
        setTimeout(() => {
            clearInterval(intervalId);
        }, 300000);
    }, [walletBalance, fetchWalletBalance]);


    const updateWalletBalance = async (amount) => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/update-wallet`, { amount }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'User-ID': userId,
                },
            });
            setWalletBalance(response.data.wallet_balance);
        } catch (error) {
            console.error('Error updating wallet balance:', error);
        }
    };

    return (
        <RechargeOptionsContext.Provider value={{ rechargeOptions, loading, error, walletBalance, updateWalletBalance, fetchWalletBalance, startBalanceCheck   }}>
            {children}
        </RechargeOptionsContext.Provider>
    );
};
